import { deleteAsync, getAsync, patchAsync, postAsync, postMultiFormDataAsync, putAsync } from '../helpers/apiRequest'
import {
  IAutomationSearchResponse,
  IEditHistory,
  IGetAutomationRequest,
  IGetAutomationResponse,
} from '../models/api/IAutomationRequest'
import {
  IAutomationUploadRequest,
  IAutomationUploadResponse,
  IAutomationLikeUploadRequest,
  IAutomationLikeUploadResponse,
  IAutomationRequestModel,
  IAutomationEditApproval,
} from '../models/api/IAutomationUploadRequest'
import { IBodyRequestModel } from '../models/api/IBodyRequestModel'
import { IAutomationLike, IManageAutomationTableRow } from '../models/api/IManageAutomationTableRow'
import { IResponse } from '../models/api/IResponse'
import { TokenExpiryWrapper } from './TokenManager'
import { ISearchDiscoverRequest } from '../models/api/ISearchRequest'
import {
  ConformActionRequiredRequestType,
  IPostUpdateApprovalRequest,
  IPostUpdateApprovalResponse,
} from '../models/api/IAutomationApprovalRequest'
import {
  IAutomationCommentRequest,
  IDeleteCommentRequest,
  IGetAutomationCommentRequest,
} from '../models/api/IAutomationCommentModel'
import { IUpdateSourcesRequest } from '../models/api/IAutomationSourcesModel'
import {
  IFeaturedAutomationResponse,
  IFeaturedAutomationsRequest,
  IFeaturedDetails,
  IUpdateFeatureLocationRequest,
} from '../models/api/IFeaturedAutomations'

export const postAutomationUpload = TokenExpiryWrapper(
  async (request: IAutomationUploadRequest): Promise<IResponse<IAutomationUploadResponse>> => {
    try {
      const formData = new FormData()
      Object.keys(request)
        .filter((key) => key !== 'token' && key !== 'AutomationFile')
        .forEach((key) => {
          if (key === 'AutomationClients' || key === 'CurrentActivityTime' || key === 'AutomatedActivityTime') {
            formData.append(key, JSON.stringify(request[key]))
            return
          }
          formData.append(key, request[key] || '')
        })

      if (request.AutomationFile) {
        formData.append('file', request.AutomationFile)
      }

      return postMultiFormDataAsync(`/v1/automations/upload`, request.token ?? '', formData)
    } catch (error) {
      const response = error as Response
      return {
        success: false,
        message: await response.text(),
      }
    }
  },
  [],
  null,
)

export const postSearchAutomation = TokenExpiryWrapper(
  async (request: ISearchDiscoverRequest): Promise<IResponse<IAutomationSearchResponse>> =>
    postAsync(`/v1/automations/search`, request),
  [],
  null,
)

export const getAutomation = TokenExpiryWrapper(
  async (request: IGetAutomationRequest): Promise<IResponse<IGetAutomationResponse>> => {
    const typeId = request.typeId ? `?typeId=${request.typeId}` : ''
    return getAsync(`/v1/automations/details/${request.id}${typeId}`, request)
  },
  [],
  null,
)

export const getManageAutomationsTableRows = TokenExpiryWrapper(
  async (request: IBodyRequestModel): Promise<IResponse<IManageAutomationTableRow[]>> =>
    getAsync(`/v1/automations/manage-list`, request),
  [],
  null,
)

export const getMyContributions = TokenExpiryWrapper(
  async (request: IBodyRequestModel): Promise<IResponse<IManageAutomationTableRow[]>> =>
    getAsync(`/v1/automations/my-contributions`, request),
  [],
  null,
)

export const getDeactivatedAutomations = TokenExpiryWrapper(
  async (request: IBodyRequestModel): Promise<IResponse<IManageAutomationTableRow[]>> =>
    getAsync(`/v1/automations/deactivated-list`, request),
  [],
  null,
)

export const postlikeAutomation = TokenExpiryWrapper(
  async (request: IAutomationLikeUploadRequest): Promise<IResponse<IAutomationLikeUploadResponse>> => {
    return postAsync(`/v1/automations/likes/${request.id}`, request)
  },
  [],
  null,
)

export const checkLikeAutomation = TokenExpiryWrapper(
  async (request: IAutomationLikeUploadRequest): Promise<IResponse<boolean>> => {
    return await getAsync(`/v1/automations/${request.id}/check-Liked`, request)
  },
  [],
  null,
)
export const getAutomationLikes = TokenExpiryWrapper(
  async (request: IAutomationLikeUploadRequest): Promise<IResponse<IAutomationLike[]>> => {
    return await getAsync(`/v1/automations/all-likes/${request.id}`, request)
  },
  [],
  null,
)

export const updateApprovals = TokenExpiryWrapper(
  async (request: IPostUpdateApprovalRequest): Promise<IResponse<IPostUpdateApprovalResponse>> =>
    postAsync(`/v1/automations/approvals`, request),
  [],
  null,
)

export const deactivateAutomation = TokenExpiryWrapper(
  async (request: IAutomationRequestModel) => patchAsync(`/v1/automations/deactivate`, request),
  [],
  null,
)

export const reactivateAutomation = TokenExpiryWrapper(
  async (request: IAutomationRequestModel) => patchAsync(`/v1/automations/reactivate`, request),
  [],
  null,
)

export const deleteAutomation = TokenExpiryWrapper(
  async (request: IAutomationRequestModel) => deleteAsync(`/v1/automations`, request),
  [],
  null,
)

export const editApproval = TokenExpiryWrapper(
  async (request: IAutomationEditApproval) => putAsync(`/v1/automations/approvals`, request),
  [],
  null,
)

export const addNewComment = TokenExpiryWrapper(
  async (request: IAutomationCommentRequest) => postAsync(`/v1/automations/comments`, request),
  [],
  null,
)

export const getAutomationComments = TokenExpiryWrapper(
  async (request: IGetAutomationCommentRequest) =>
    getAsync(`/v1/automations/comments/${request.automationId}`, request),
  [],
  null,
)

export const getAutomationCommentHistory = TokenExpiryWrapper(
  async (request: IGetAutomationCommentRequest) =>
    getAsync(`/v1/automations/comment-history/${request.automationId}`, request),
  [],
  null,
)

export const deleteAutomationComment = TokenExpiryWrapper(
  async (request: IDeleteCommentRequest) => deleteAsync(`/v1/automations/comments/${request.commentId}`, request),
  [],
  null,
)

export const getAutomationEditHistory = TokenExpiryWrapper(
  async (request: IAutomationRequestModel): Promise<IResponse<IEditHistory[]>> => {
    return await getAsync(`/v1/automations/edit-history/${request.automationId}`, request)
  },
  [],
  null,
)

export const confirmActionRequired = TokenExpiryWrapper(
  async (request: ConformActionRequiredRequestType) => postAsync(`/v1/automations/confirm-action`, request),
  [],
  null,
)

export const getAutomationSources = TokenExpiryWrapper(
  async (request: IBodyRequestModel) => getAsync(`/v1/automations/sources`, request),
  [],
  null,
)

export const updateAutomationSource = TokenExpiryWrapper(
  (request: IUpdateSourcesRequest): Promise<IResponse<{ message: string }>> => {
    return putAsync(`/v1/automations/sources`, request)
  },
  [],
  {
    success: false,
    message: 'Failed to update automation source',
  },
)

export const updateFeaturedAutomation = TokenExpiryWrapper(
  async (request: IFeaturedAutomationsRequest): Promise<IResponse<{ message: string }>> => {
    try {
      const formData = new FormData()

      for (const position in request) {
        if (position === 'token') continue
        const data = request[position as keyof IFeaturedAutomationsRequest] as IFeaturedDetails
        if (!data) continue
        // Append files
        if (data.bannerFile) formData.append(`${position}BannerFile`, data.bannerFile)
        if (data.thumbnailFile) formData.append(`${position}ThumbnailFile`, data.thumbnailFile)
      }

      const { token, ...featureDetails } = request

      const newObject: { [key: string]: { Id: string; UploadTypeId: number | null; ContentData: string } } = {}

      for (const [key, value] of Object.entries(featureDetails)) {
        if (value?.automation) {
          const { automation, content } = value
          const { Id, UploadTypeId } = automation

          newObject[key] = { Id, UploadTypeId, ContentData: content }
        }
      }

      formData.append(`formDetails`, JSON.stringify(newObject))

      return postMultiFormDataAsync(`/v1/featured`, token ?? '', formData)
    } catch (error) {
      const response = error as Response
      return {
        success: false,
        message: await response.text(),
      }
    }
  },
  [],
  null,
)

export const getFeaturedAutomations = TokenExpiryWrapper(
  async (request: IBodyRequestModel) => getAsync(`/v1/featured`, request),
  [],
  null,
)

export const getFeatured = TokenExpiryWrapper(
  async (request: IGetAutomationRequest): Promise<IResponse<IFeaturedAutomationResponse>> => {
    const typeId = request.typeId ? `?typeId=${request.typeId}` : ''
    return getAsync(`/v1/featured/details/${request.id}${typeId}`, request)
  },
  [],
  null,
)

export const getFeaturedContent = TokenExpiryWrapper(
  async (request: IGetAutomationRequest) => getAsync(`/v1/featured/content/${request.id}`, request),
  [],
  null,
)

export const updateFeatureLocation = TokenExpiryWrapper(
  async (request: IUpdateFeatureLocationRequest) => patchAsync(`/v1/featured/featureLocation`, request),
  [],
  null,
)
